/**
 * Query keys and small pure helpers shared by the buying screens.
 *
 * Keys are hierarchical so a receive on one PO can invalidate the PO detail,
 * every PO list, the summary tiles and the cost-approval queue with three
 * `invalidateQueries` calls instead of a hand-written list per screen.
 */

import type { QueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api/client';
import type { ProductDetail } from '@/lib/api/types';
import type {
  CaseModeLine,
  ConfirmableLine,
  ConfirmLine,
  CreatedProduct,
  CreateProductPrefill,
  LineEnrichmentApply,
  MatchCandidate,
  RemainderLine,
} from './types';

export const poKeys = {
  all: ['purchase-orders'] as const,
  list: (filters: Record<string, unknown>) =>
    ['purchase-orders', 'list', filters] as const,
  summary: (filters: Record<string, unknown>) =>
    ['purchase-orders', 'summary', filters] as const,
  detail: (id: number) => ['purchase-orders', 'detail', id] as const,
};

export const vendorKeys = {
  all: ['vendors'] as const,
  list: (filters: Record<string, unknown>) => ['vendors', 'list', filters] as const,
  detail: (id: number) => ['vendors', 'detail', id] as const,
};

export const costChangeKeys = {
  all: ['cost-changes'] as const,
  list: (filters: Record<string, unknown>) => ['cost-changes', 'list', filters] as const,
  count: ['cost-changes', 'count'] as const,
};

export const invoiceDuplicateKeys = {
  all: ['invoice-duplicates'] as const,
  check: (vendorId: number, key: string, excludePoId: number | null) =>
    ['invoice-duplicates', vendorId, key, excludePoId] as const,
};

export const importKeys = {
  all: ['invoice-imports'] as const,
  list: (filters: Record<string, unknown>) =>
    ['invoice-imports', 'list', filters] as const,
  detail: (id: number) => ['invoice-imports', 'detail', id] as const,
  /** Own root on purpose: the stored upload never changes, so invalidating
   *  `all` (Re-match, Re-parse, confirm) must not re-download it. */
  file: (id: number) => ['invoice-import-file', id] as const,
};

/**
 * Anything that moves stock or PO state. Receiving also rewrites product cost
 * (or parks a pending cost change), so inventory and the approval queue go too.
 */
export function invalidateAfterReceive(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: poKeys.all });
  // A new order may now carry an invoice number someone is about to type again.
  void qc.invalidateQueries({ queryKey: invoiceDuplicateKeys.all });
  void qc.invalidateQueries({ queryKey: costChangeKeys.all });
  void qc.invalidateQueries({ queryKey: ['inventory'] });
  void qc.invalidateQueries({ queryKey: ['products'] });
}

/* ── Numeric coercion ─────────────────────────────────────────────────────────
   Postgres `numeric` reaches the browser as a string (`orderedQty`,
   `receivedQty`, `pctChange`, `shippedWeightLbs`). Every read goes through
   here so no screen ever compares "10" < 9 lexically.                        */

export function n(value: number | string | null | undefined, fallback = 0): number {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = typeof value === 'string' ? Number.parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** The AI parser emits dollars. Everything downstream is integer cents. */
export function dollarsToCents(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) return 0;
  return Math.round(value * 100);
}

/** Percentage change between two costs, or null when the base is unusable. */
export function pctDelta(from: number | null | undefined, to: number): number | null {
  if (from === null || from === undefined || from <= 0) return null;
  return ((to - from) / from) * 100;
}

/* ── Case mode ────────────────────────────────────────────────────────────────
   Mirrors `server/admin/src/lib/utils/invoice-case-mode.ts`. The operator reads
   an invoice in cases; the database stores eaches. Case mode is a display
   translator only — it collapses back to per-each at the submit boundary, and
   this is the single place that conversion happens.                          */

export function caseModeSubtotalCents(line: CaseModeLine): number {
  return line.unitCostCents * line.qty;
}

/** Per-each cost implied by a per-case cost, or null when it cannot divide. */
export function derivedPerEachCents(
  line: Pick<CaseModeLine, 'caseMode' | 'unitsPerCase' | 'unitCostCents'>
): number | null {
  if (!line.caseMode) return null;
  const upc = line.unitsPerCase ?? 0;
  if (upc <= 1) return null;
  return line.unitCostCents / upc;
}

/** Collapse a review line to the per-each shape `/ai/confirm-invoice` wants. */
export function submitPayloadForLine(line: CaseModeLine): {
  qty: number;
  orderedQty: number;
  unitCostCents: number;
} {
  if (line.caseMode && line.unitsPerCase && line.unitsPerCase > 1) {
    const upc = line.unitsPerCase;
    return {
      qty: line.qty * upc,
      orderedQty: line.orderedQty * upc,
      unitCostCents: Math.round(line.unitCostCents / upc),
    };
  }
  return {
    qty: line.qty,
    orderedQty: line.orderedQty,
    unitCostCents: line.unitCostCents,
  };
}

/**
 * "Keep current margin": scale retail by the cost ratio so margin % is
 * unchanged. Null when either side of the ratio is missing, because a
 * suggested price built on a zero cost is a nonsense number.
 */
export function keepMarginPriceCents(
  currentPriceCents: number | null,
  currentCostCents: number | null,
  newCostCents: number
): number | null {
  if (!currentPriceCents || currentPriceCents <= 0) return null;
  if (!currentCostCents || currentCostCents <= 0) return null;
  if (newCostCents <= 0) return null;
  return Math.round((currentPriceCents * newCostCents) / currentCostCents);
}

/* ── TASK-0191: partial confirm payload ──────────────────────────────────────
   One line either goes onto the PO right now (has a product AND the
   operator left it checked) or it doesn't — and "doesn't" is no longer a
   silent drop. Every line NOT going onto the PO rides along as a
   `remainderLine`, so the server can spin it into a follow-up draft import.
   This is the one place that split happens, so the "nothing excluded"
   happy path (every matched line still `included`) can be pinned by a
   test to produce byte-identical `lineItems` to the pre-TASK-0191 code.  */

export type ConfirmPayload = {
  lineItems: ConfirmLine[];
  remainderLines: RemainderLine[];
};

/**
 * TASK-0242 (FR-IMPORT-B): a line whose product is the matcher's guess (a name match, or a PO
 * line found by name), not a barcode, item-number or PO-line match and not a person's choice.
 * Every way a person picks a product records it as `high` (see the review screen).
 */
export function isGuessedMatch(
  line: Pick<ConfirmableLine, 'productId' | 'confidence'>
): boolean {
  return line.productId !== null && line.confidence !== 'high';
}

/** The line's suggestions with its guessed product first (it is one of them unless the API changes). */
function suggestionsWithGuess(line: ConfirmableLine): MatchCandidate[] {
  if (line.productId === null) return line.candidates;
  const others = line.candidates.filter((c) => c.productId !== line.productId);
  const own = line.candidates.find((c) => c.productId === line.productId) ?? {
    productId: line.productId,
    productName: line.productName,
    confidence: line.confidence,
    method: line.matchMethod,
  };
  return [own, ...others];
}

/**
 * TASK-0243 (FR-LEARN): the link holds a different, non-blank item number than the one the invoice
 * printed for this line's product. The reviewer is asked "keep / use invoice"; nothing is replaced
 * unless they choose the invoice's.
 */
export function itemNumberConflict(
  line: Pick<ConfirmableLine, 'vendorSku' | 'printedSku'>
): { link: string; invoice: string } | null {
  const invoice = line.printedSku ?? null;
  const link = (line.vendorSku ?? '').trim();
  if (invoice === null || link === '' || link === invoice) return null;
  return { link, invoice };
}

/**
 * TASK-0243 (FR-LEARN): what a hand-matched line (a person picked, searched or created its product:
 * `manual`) saves on the vendor link at confirm: the printed item number, and the printed pack when
 * it is 2 or more. The full `enrichmentApply` contract the API takes; never size, never pack
 * decomposition and never `update_product` (D-23: the product's case quantity is not a pack source).
 * Null for every other line: machine matches (barcode, item number, PO line) send nothing, as before.
 * The item number is left off when another product of the vendor holds it, and when the link holds a
 * different one the reviewer did not choose to replace.
 */
export function handMatchEnrichment(
  line: Pick<
    ConfirmableLine,
    | 'productId'
    | 'matchMethod'
    | 'unit'
    | 'vendorSku'
    | 'printedSku'
    | 'printedPack'
    | 'skuOwnerId'
    | 'skuChoice'
  >
): LineEnrichmentApply | null {
  if (line.productId === null || line.matchMethod !== 'manual') return null;
  const printed = line.printedSku ?? null;
  const heldByAnother = line.skuOwnerId != null && line.skuOwnerId !== line.productId;
  const conflict = itemNumberConflict(line);
  const replace = conflict !== null && line.skuChoice === 'use';
  const saveSku = printed !== null && !heldByAnother && (conflict === null || replace);
  const pack = line.printedPack ?? null;
  const savePack =
    line.unit === 'ea' && pack !== null && Number.isInteger(pack) && pack >= 2;
  return {
    size: false,
    pack: false,
    vendorSku: saveSku,
    caseQtyResolution: savePack ? 'set_vendor_override' : 'none',
    // Review R2-3: the number the reviewer saw goes with the replace, so a later correction is not overwritten.
    ...(saveSku && replace
      ? { replaceVendorSku: true, expectedVendorSku: line.vendorSku ?? null }
      : {}),
  };
}

export function buildConfirmPayload(lines: ConfirmableLine[]): ConfirmPayload {
  const lineItems: ConfirmLine[] = [];
  const remainderLines: RemainderLine[] = [];

  for (const line of lines) {
    const payload = submitPayloadForLine(line);
    const unitCostCents = Math.max(0, Math.round(payload.unitCostCents));

    if (line.productId !== null && line.included) {
      const learn = handMatchEnrichment(line);
      lineItems.push({
        productId: line.productId,
        qty: payload.qty,
        orderedQty: payload.orderedQty,
        unitCost: unitCostCents,
        unit: line.unit,
        ...(line.shippedCaseCount != null
          ? { shippedCaseCount: line.shippedCaseCount }
          : {}),
        ...(line.shippedWeightLbs != null
          ? { shippedWeightLbs: line.shippedWeightLbs }
          : {}),
        enrichmentLineKey: line.key,
        // TASK-0243 (FR-LEARN): a hand match teaches the vendor link; other lines send nothing.
        ...(learn ? { enrichmentApply: learn } : {}),
        // TASK-0191 round 2 (review fix): `line.key` is the original
        // parse-line index (set once in `buildLines`, never renumbered —
        // lines are only ever patched in place). Always sent so the
        // server can enforce full lineItems/remainderLines coverage
        // whenever this confirm also carries remainderLines; ignored by
        // the server on a plain full confirm (remainderLines empty).
        lineIndex: line.key,
      });
      continue;
    }

    // TASK-0242: the server records any held-back line that carries a product as a person's
    // choice (high, manual), so the follow-up import would start it ticked. A guess nobody
    // ticked is not a choice: it travels without a product, its guess first among the
    // suggestions, and the follow-up asks again.
    const guess = isGuessedMatch(line);
    remainderLines.push({
      productId: guess ? null : line.productId,
      productName: guess ? '' : line.productName,
      description: line.description,
      sku: line.sku || null,
      upc: line.upc || null,
      qty: payload.qty,
      orderedQty: payload.orderedQty,
      unitCostCents,
      unit: line.unit,
      unitsPerCase: line.unitsPerCase,
      vendorSku: line.vendorSku,
      shippedCaseCount: line.shippedCaseCount,
      shippedWeightLbs: line.shippedWeightLbs,
      confidence: line.confidence,
      matchMethod: line.matchMethod,
      candidates: guess ? suggestionsWithGuess(line) : line.candidates,
      warnings: line.warnings,
      lineIndex: line.key,
    });
  }

  return { lineItems, remainderLines };
}

/**
 * TASK-0242 (FR-IMPORT-B): what a line takes from the product a person picked from its
 * suggestions, read the way the API's matcher reads it (`enrichMatchedProduct`): the product's
 * unit and retail price, and as the system cost the vendor's own unit cost when this vendor
 * carries the product, else the product's cost. Display and comparison only: it never writes cost.
 */
export function productFieldsForLine(
  detail: Pick<ProductDetail, 'price' | 'cost' | 'soldByWeight' | 'vendors'> & {
    unit?: string | null;
  },
  vendorId: number | null
): {
  productUnit: 'ea' | 'lb';
  currentPriceCents: number | null;
  systemUnitCostCents: number | null;
  /** TASK-0243: the item number this vendor's link holds for the product (null: no link or none). */
  vendorSku: string | null;
  /** TASK-0243 review R1-7: the pack this vendor's link holds for the product (null: none). */
  linkPack: number | null;
} {
  const link =
    vendorId === null
      ? undefined
      : (detail.vendors ?? []).find((v) => v.vendorId === vendorId);
  return {
    productUnit:
      detail.unit === 'lb' || detail.unit === 'ea'
        ? detail.unit
        : detail.soldByWeight
          ? 'lb'
          : 'ea',
    currentPriceCents: detail.price ?? null,
    systemUnitCostCents: link?.unitCost ?? detail.cost ?? null,
    vendorSku: link?.vendorSku ?? null,
    linkPack: link?.unitsPerCase ?? null,
  };
}

/* ── TASK-0191: inline + bulk product creation ───────────────────────────────
   Mirrors the Svelte admin's `/products/new?...` create action (prefill →
   `POST /products` → best-effort `POST /vendors/:id/items`), minus the
   second browser tab: the caller auto-attaches the result to the line that
   triggered it. The vendor-item link is a SECONDARY action — its failure
   surfaces as a warning on the returned result but never unwinds the
   product create, matching the Svelte reference exactly.                    */

export async function createProductAndLink(
  vendorId: number | null,
  prefill: CreateProductPrefill
): Promise<{ product: CreatedProduct; linkWarning: string | null }> {
  // TASK-0191 round 3 (review fix, finding 1): a `unit: 'lb'` line used to
  // create a `fixed`/eaches product regardless — the auto-attach then
  // immediately tripped the unit-mismatch guard it exists to avoid, for
  // any weighed-goods vendor. `productBodySchema` already accepts both
  // fields (`server/api/src/modules/catalog/routes.ts`), so this is a
  // client-only fix.
  const soldByWeight = prefill.unit === 'lb';
  // TASK-0259 (FRESH-1): an input product has no price (the server forces 0 and refuses one) and needs a
  // family; everything else about the create is the same.
  const input = prefill.purchasingOnly === true;
  const product = await api.post<CreatedProduct>('/products', {
    name: prefill.name,
    ...(input
      ? { purchasingOnly: true, productionFamily: prefill.productionFamily ?? null }
      : { price: prefill.price }),
    ...(prefill.departmentId != null ? { departmentId: prefill.departmentId } : {}),
    priceType: soldByWeight ? 'weight' : 'fixed',
    soldByWeight,
    taxable: false,
    taxExempt: false,
    ebtEligible: true,
    wicEligible: false,
    isPreparedFood: false,
    storageType: 'shelf',
    barcodes: prefill.barcode
      ? [{ code: prefill.barcode, type: 'upc', isPrimary: true }]
      : [],
    ...(prefill.cost != null ? { cost: prefill.cost } : {}),
  });

  let linkWarning: string | null = null;
  if (vendorId != null) {
    try {
      await api.post(`/vendors/${vendorId}/items`, {
        productId: product.id,
        isPreferred: true,
        ...(prefill.vendorSku ? { vendorSku: prefill.vendorSku } : {}),
        ...(prefill.unitsPerCase != null ? { unitsPerCase: prefill.unitsPerCase } : {}),
        ...(prefill.caseCost != null ? { caseCost: prefill.caseCost } : {}),
        // Mirrors the per-each `cost` onto the vendor link too, same as the
        // Svelte create action's `autoLinkCaseCost`/`cost` passthrough.
        ...(prefill.cost != null ? { unitCost: prefill.cost } : {}),
      });
    } catch (error) {
      linkWarning = `Product created, but the vendor link failed: ${errorMessage(error)}. Link it by hand from the vendor page.`;
    }
  }

  return { product, linkWarning };
}

export type BulkCreateLine = { key: number } & CreateProductPrefill;

export type BulkCreateResult =
  | { key: number; ok: true; product: CreatedProduct; linkWarning: string | null }
  | { key: number; ok: false; error: string };

/**
 * TASK-0191 round 3 (review fix, finding 3). The incident this task fixes
 * was 61 lines — unbounded `Promise.allSettled` over N lines means N×2
 * simultaneous requests (product create + vendor link) with no cap, which
 * degrades badly at that size. A small worker-pool `pMap` keeps at most
 * `BULK_CREATE_CONCURRENCY` `createProductAndLink` calls in flight at
 * once, no matter how many lines are selected — not worth a dependency
 * for.
 */
export const BULK_CREATE_CONCURRENCY = 6;

async function pMap<In, Out>(
  items: In[],
  concurrency: number,
  fn: (item: In, index: number) => Promise<Out>
): Promise<PromiseSettledResult<Out>[]> {
  const results: PromiseSettledResult<Out>[] = new Array(items.length);
  let next = 0;
  async function worker() {
    for (;;) {
      const index = next++;
      if (index >= items.length) return;
      try {
        results[index] = { status: 'fulfilled', value: await fn(items[index]!, index) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  }
  await Promise.all(
    Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, worker)
  );
  return results;
}

/**
 * N lines → N `createProductAndLink` calls, batched `BULK_CREATE_CONCURRENCY`
 * at a time. One failing row (a duplicate name the API rejects, a network
 * blip) must not block the others — same spirit as the existing per-item
 * price-update failure reporting on this screen — so this always resolves,
 * never rejects; the caller reads `ok` per row.
 */
export async function createProductsForLines(
  vendorId: number | null,
  lines: BulkCreateLine[]
): Promise<BulkCreateResult[]> {
  const settled = await pMap(lines, BULK_CREATE_CONCURRENCY, (line) =>
    createProductAndLink(vendorId, line)
  );
  return settled.map((result, index) => {
    const key = lines[index]!.key;
    if (result.status === 'fulfilled') {
      return {
        key,
        ok: true,
        product: result.value.product,
        linkWarning: result.value.linkWarning,
      };
    }
    return { key, ok: false, error: errorMessage(result.reason) };
  });
}

/* ── TASK-0191 round 3 (review fix, finding 5): actionable remainder toast ───
   Confirming a subset (the common case) navigates to the new PO, leaving
   the remainder import reachable only by remembering its number and
   finding it by eye in the inbox — unlike the "confirm 0 of N" path,
   which already navigates straight to it. This builds the sonner `action`
   for the confirm toast so a click reaches it the same way.               */

export function remainderToastAction(
  remainderImportId: number | null,
  goToImport: (importId: number) => void
): { label: string; onClick: () => void } | undefined {
  if (remainderImportId == null) return undefined;
  return {
    label: `Open import #${remainderImportId}`,
    onClick: () => goToImport(remainderImportId),
  };
}
